<template>
    <div>
        <p class="mb-8 first-letter:capitalize font-semibold text-black">{{ $t('mailer.beneficiaries') }}</p>
        <template v-if="fetchingData">
            <loading/>
        </template>
        <template>
            <ul
                class="relative bg-primary-5 border border-primary-20 p-8"
                :class="editApplicants ? 'rounded-t grid grid-cols-3 gap-8' : 'rounded flex flex-wrap'"
            >
                <li v-if="!editApplicants && canEdit" class="absolute right-0 top-0 m-10 cursor-pointer" @click="startEdit">
                    <svg-icon type="edit" class="w-16 h-16"/>
                </li>
                <li
                    v-for="application in shownApplications"
                    :key="application.id"
                    class="bg-white rounded border overflow-hidden px-12 py-8 flex items-center"
                    :class="editApplicants ? 'group relative w-full grow hover:border-semantic-error cursor-pointer hover:bg-gradient-to-tr hover:from-primary-5 hover:to-transparent hover:animate-pulse transition-[border]' : 'w-auto border-primary-20 cursor-default mr-8 my-4'"
                    @click="editApplicants ? removeApplicationFromSelection(application) : () => {}"
                >
                    <div class="inline-flex items-center grow">
                        <avatar
                            :color="application.stageColor"
                            :image="application.image_url"
                            size="small"
                        >
                            {{ application.initials }}
                        </avatar>
                        <span class="me-4 ms-6">{{ application.fullName }}</span>
                    </div>
                    <div v-if="editApplicants">
                        <svg-icon type="delete"
                                  class="w-16 h-16 text-semantic-error absolute -right-32 group-hover:right-0 top-1/2 -translate-x-1/2 -translate-y-1/2 transition-transform ease-in-out duration-300"/>
                    </div>
                </li>
                <li
                    v-if="!editApplicants && applications.length > limit"
                    class="h-48 inline-flex items-center mr-8 my-4 p-6 px-10 bg-white hover:bg-primary-5 rounded border border-primary-20 cursor-pointer"
                    @click="showAll = !showAll"
                >
                    <span>{{ showAll ? $tc("mailer.show_first_count", limit, { count: limit}) : $t("mailer.show_all") }}</span>
                </li>
                <simple-button
                    class="my-auto"
                    icon="add"
                    look="icon"
                    v-else-if="editApplicants && canEdit"
                    @click="addRecipients = true"
                />
            </ul>
            <div v-if="editApplicants && canEdit"
                 class="p-8 rounded-b bg-white border border-t-0 border-primary-20 flex justify-end space-x-12">
                <simple-button look="secondary" @click="cancelEdit">
                    {{ $t("general.cancel") }}
                </simple-button>
                <simple-button display="secondary" look="primary" icon="save" @click="saveEdit">
                    {{ $t("general.save") }}
                </simple-button>
            </div>
        </template>
        <popup-add-recipient
            v-if="addRecipients"
            :temp-applications="tempApplications"
            @close="addRecipients = false"
            @save="addToRecipients"
        />
    </div>
</template>

<script>
import Avatar from "../../ui/Avatar.vue";
import loading from "../../ui/Loading.vue";
import SimpleButton from "../../ui/SimpleButton.vue";
import SvgIcon from "../../ui/SvgIcon.vue";
import styles from "../../../../../../../styles.config";
import PopupModal from "../../ui/PopupModal.vue";
import PopupModalConfirm from "../../ui/PopupModalConfirm.vue";
import Input from "../../forms/Input.vue";
import * as applicationsService from "../../../services/ApplicationService";
import Application from "../../Application.vue";
import Checkbox from "../../forms/Checkbox.vue";
import {normalizeJson} from "../../../../shared/helpers";
import ApplicationModel from "../../../models/Application";
import PopupAddRecipient from "./PopupAddRecipient.vue";

export default {
    props: {
        applications: {
            type: Array,
            required: true
        },
        totalApplications: {
            type: Number,
            required: true
        },
        applicationsCount: {
            type: Number,
            required: true
        },
        isBulkBasedOnCount: {
            type: Boolean,
            required: true
        },
        fetchingData: {
            type: Boolean,
            required: true
        },
        canEdit: {
            type: Boolean,
            required: true,
        }
    },
    name: "recipients",
    components: {
        PopupAddRecipient,
        Checkbox, Application, Input, PopupModalConfirm, PopupModal, SvgIcon, SimpleButton, loading, Avatar
    },
    data: function () {
        return {
            timeout: null,
            showAll: false,
            limit: 4,
            editApplicants: false,
            tempApplications: [],
            removeApplications: [],
            addRecipients: false,
            name: "",
            job: "",
            filteredApplications: [],
        }
    },
    computed: {
        styles() {
            return styles
        },
        shownApplications() {
            if (!this.editApplicants) {
                if (this.showAll) {
                    return this.applications.filter(i => i.email);
                } else {
                    return this.applications.filter(i => i.email).slice(0, this.limit);
                }
            } else {
                return this.tempApplications;
            }
        },
    },
    methods: {
        removeApplicationFromSelection(application) {
            this.removeApplications = [...this.removeApplications, application];
            this.tempApplications = this.tempApplications.filter(i => i.id !== application.id);
        },
        startEdit() {
            this.editApplicants = true;
            this.tempApplications = this.applications.filter(i => i.email);
        },
        cancelEdit() {
            this.editApplicants = false;
            this.tempApplications = [];
            this.removeApplications = [];
        },
        saveEdit() {
            this.showAll = true;
            this.editApplicants = false;
            this.$emit("updateApplicants", this.tempApplications);
            this.tempApplications = [];
        },
        addToRecipients(recipients) {
            if (recipients.length > 0) {
                // check if any of the recipients is in the tempApplications
                recipients.forEach((recipient) => {
                    if (!this.tempApplications.find((application) => application.id === recipient.id)) {
                        this.tempApplications.push(recipient);
                    }
                });
            }
            this.addRecipients = false;
            this.saveEdit();
        }
    },
}
</script>

<style scoped>
.fade:hover {
    animation: fade 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes fade {
    0% {
        opacity: .5;
    }
    50% {
        opacity: .2;
    }
    100% {
        opacity: .5;
    }
}
</style>
